<template>
  <div class="vote-summary">
    <span v-if="!voteRecord.url">
      <p>Name: <strong>{{ displayName }}</strong></p>
      <p>Total shares: <strong>{{ displayShares }}</strong></p>

      <!-- New (data-driven) format -->
      <div v-if="hasAnswers">
        <div
          v-for="(answer, questionId) in voteRecord.vote.answers"
          :key="questionId"
          class="answer-block"
        >
          <p v-if="questionLabels[questionId]" class="q-label">
            <strong>{{ questionLabels[questionId] }}</strong>
          </p>
          <p>
            Voted:
            <span v-if="answer.choice === 'a'"><strong>BOX A - DISCRETIONARY</strong></span>
            <span v-else-if="answer.choice === 'b'">
              <strong>BOX B - DIRECTED</strong>
              <span
                v-for="(val, cid) in answer.directed"
                :key="cid"
                class="indent"
                style="display: block;"
              >
                {{ candidateName(questionId, cid) }}
                <strong>{{ val || 0 }}</strong>
              </span>
            </span>
          </p>
        </div>
      </div>

      <!-- Legacy format -->
      <p v-else>
        Voted:
        <span v-if="voteRecord.vote.vote === 'a'"><strong>BOX A - DISCRETIONARY</strong></span>
        <span v-else-if="voteRecord.vote.vote === 'b'">
          <strong>BOX B - DIRECTED</strong>
          <span
            v-for="(val, cid) in voteRecord.vote.directed || {}"
            :key="cid"
            class="indent"
            style="display: block;"
          >
            {{ legacyCandidateName(cid) }}
            <strong>{{ val || 0 }}</strong>
          </span>
        </span>
      </p>

      <p v-if="voteRecord.vote.signcustodian">
        Custodian for: <strong>{{ voteRecord.vote.custodian }}</strong>
      </p>
      <div v-if="voteRecord.vote.sigx">
        Signed name with an "x":
        <p class="indent">Witness: <strong>{{ voteRecord.vote.witness1 }}</strong></p>
        <p class="indent">Witness: <strong>{{ voteRecord.vote.witness2 }}</strong></p>
      </div>
      <img v-if="voteRecord.sig" class="sig" :src="voteRecord.sig" />
    </span>

    <span v-else>
      <p>You uploaded a proxy form {{ formatDate(voteRecord.createdOn) }}.</p>
      <br />
      <p>
        <a :href="voteRecord.url" target="_blank">Click here</a>
        to view your uploaded proxy.
      </p>
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useMeetingStore } from '@/stores/meeting'
import moment from 'moment'

const props = defineProps({
  voteRecord: { type: Object, required: true },
  displayName: { type: String, default: '' },
  displayShares: { type: [Number, String], default: '' },
})

const meetingStore = useMeetingStore()

const hasAnswers = computed(
  () =>
    props.voteRecord?.vote?.answers &&
    typeof props.voteRecord.vote.answers === 'object' &&
    Object.keys(props.voteRecord.vote.answers).length > 0,
)

// Lookup question/candidate labels from the current ballot if it's still
// loaded. Falls back to the raw id when labels aren't available.
const ballotComponents = computed(() => meetingStore.currentBallot?.components || [])

const questionLabels = computed(() => {
  const map = {}
  for (const c of ballotComponents.value) {
    if (c.type === 'ballotQuestion') map[c.id] = c.props?.title || ''
  }
  return map
})

function candidateName(questionId, candidateId) {
  const q = ballotComponents.value.find(
    (c) => c.type === 'ballotQuestion' && c.id === questionId,
  )
  if (!q) return candidateId
  const cand = (q.props?.candidates || []).find((c) => c.id === candidateId)
  return cand?.name || candidateId
}

// Legacy candidate map — the old code stored candidates as a fixed object
// with keys like 'nancy_luke_andrew' in Vuex. After the migration those keys
// won't be in the store anymore, so we humanize them for display.
function legacyCandidateName(key) {
  if (!key) return ''
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function formatDate(val) {
  if (!val) return '-'
  const d = val.toDate ? val.toDate() : new Date(val)
  return moment(d).fromNow()
}
</script>

<style lang="scss" scoped>
.vote-summary {
  .indent {
    padding-left: 20px;
  }

  .answer-block {
    margin-top: 12px;
  }

  .q-label {
    margin-bottom: 4px;
  }

  .sig {
    border: 2px solid #efefef;
    border-radius: 10px;
    max-width: 300px;
    margin-top: 8px;
    // Signature PNGs are transparent with dark ink; in dark mode the strokes
    // vanish against the page, so we paint a medium-gray plate behind them.
    background: var(--sig-bg, #fff);
  }
}
</style>
